import { useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User, MapPin, FileText, UploadCloud, X, Loader2, ArrowRight, Menu,
  MessageSquare, CalendarDays, Info, ClipboardList,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { TopoBackground } from "@/components/TopoBackground";
import { MobileNav } from "@/components/MobileNav";
import { useSiteImages } from "@/lib/siteImages";
import {
  ISLANDS, COUNTRIES, CONTACT_METHODS, PROJECT_TYPES, WORK_REQUIRED,
  CONSTRUCTION_STAGES, ACCEPTED_TYPES, SERVICE_OPTIONS,
} from "@/lib/constants";
import { uploadFile, createRequest } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuth";

const SIDEBAR_IMG =
  "https://static.prod-images.emergentagent.com/jobs/83fd665c-12e1-46ec-b34b-7c468a7eac31/images/b2f6e5ab41253c81562fd816070a774e44fec66d2c9f35c709d8c5d3b4a40c46.jpeg";

const inputCls =
  "h-11 rounded-md border-[#d2dad5] bg-white text-[#1a221e] placeholder:text-[#9aa5a0] focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0";
const textareaCls =
  "rounded-md border-[#d2dad5] bg-white placeholder:text-[#9aa5a0] focus-visible:ring-2 focus-visible:ring-[#c8a452] focus-visible:ring-offset-0";

const Brand = ({ light }) => (
  <Link to="/" className="flex items-center gap-3">
    <img src="/data-earth-logo.webp" alt="Data Earth Surveying" className="h-11 w-11 object-contain" />
    <span className="leading-none">
      <span className={`block font-serif text-lg font-bold tracking-[0.06em] sm:text-xl ${light ? "text-white" : "text-[#163323]"}`}>
        DATA EARTH
      </span>
      <span className="mt-0.5 block text-[10px] font-medium uppercase tracking-[0.34em] text-[#c8a452]">
        Surveying
      </span>
    </span>
  </Link>
);

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-[#1a221e]">
      {label} {required && <span className="text-[#c0392b]">*</span>}
    </Label>
    {children}
  </div>
);

const SectionHead = ({ icon: Icon, title, eyebrow }) => (
  <div className="flex items-center gap-4">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#163323] text-white">
      <Icon className="h-5 w-5" />
    </span>
    <h2 className="whitespace-nowrap font-serif text-2xl font-bold text-[#163323]">{title}</h2>
    <span className="h-px flex-1 bg-[#c8a452]/60" />
    <span className="hidden whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.25em] text-[#9aa5a0] sm:block">
      {eyebrow}
    </span>
  </div>
);

export default function RequestForm() {
  const navigate = useNavigate();
  const { openModal } = useAdminAuth();
  const { img } = useSiteImages();
  const [searchParams] = useSearchParams();
  const rawService = searchParams.get("service");
  const initialService = SERVICE_OPTIONS.includes(rawService) ? rawService : "";

  const [form, setForm] = useState({
    full_name: "", company_name: "", phone: "", email: "", preferred_contact_method: "Call",
    address: "", island: "", lot_parcel_number: "", property_size: "", site_access_instructions: "",
    service_type: initialService,
    project_type: "", work_required: [], construction_stage: "",
    preferred_service_date: "", project_description: "", contractor_info: "",
  });
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const isConstruction = form.service_type === "Construction Surveying";

  const locationOptions = useMemo(() => (
    <>
      <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#5a6660]">Bahamas Islands</div>
      {ISLANDS.filter((i) => i !== "Other").map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
      <div className="mt-1 border-t border-[#e6eae7] px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#5a6660]">Other Countries</div>
      {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
    </>
  ), []);

  const toggleWork = (item) =>
    setForm((f) => ({
      ...f,
      work_required: f.work_required.includes(item)
        ? f.work_required.filter((w) => w !== item)
        : [...f.work_required, item],
    }));

  const handleFiles = async (fileList) => {
    for (const file of Array.from(fileList)) {
      const tempId = `${Date.now()}-${file.name}`;
      setFiles((p) => [...p, { tempId, original_filename: file.name, size: file.size, progress: 0, uploading: true }]);
      try {
        const res = await uploadFile(file, (pr) =>
          setFiles((p) => p.map((f) => (f.tempId === tempId ? { ...f, progress: pr } : f))));
        setFiles((p) => p.map((f) => (f.tempId === tempId ? { ...f, ...res, uploading: false, progress: 100 } : f)));
      } catch {
        toast.error(`Could not upload ${file.name}`);
        setFiles((p) => p.filter((f) => f.tempId !== tempId));
      }
    }
  };
  const removeFile = (tempId) => setFiles((p) => p.filter((f) => f.tempId !== tempId));
  const humanSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error("Full name is required");
    if (!form.phone.trim()) return toast.error("Phone number is required");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("A valid email address is required");
    if (!form.address.trim()) return toast.error("Property address or location is required");
    if (!form.island) return toast.error("Please select an island or country");
    if (!form.service_type) return toast.error("Please select a service");
    if (!form.project_description.trim()) return toast.error("Project description is required");
    if (isConstruction && !form.project_type) return toast.error("Select a project type");
    if (isConstruction && form.work_required.length === 0) return toast.error("Select at least one work item");
    if (!confirmed) return toast.error("Please confirm the information is accurate");

    setSubmitting(true);
    try {
      const documents = files.filter((f) => !f.uploading && f.id).map((f) => ({
        id: f.id, storage_path: f.storage_path, original_filename: f.original_filename,
        content_type: f.content_type, size: f.size,
      }));
      const res = await createRequest({
        service_type: form.service_type,
        customer: {
          full_name: form.full_name, company_name: form.company_name, phone: form.phone,
          email: form.email, preferred_contact_method: form.preferred_contact_method,
        },
        location: {
          address: form.address, island: form.island, lot_parcel_number: form.lot_parcel_number,
          property_size: form.property_size, site_access_instructions: form.site_access_instructions,
        },
        construction: {
          project_type: form.project_type, work_required: form.work_required,
          construction_stage: form.construction_stage, preferred_service_date: form.preferred_service_date,
          project_description: form.project_description, contractor_info: form.contractor_info,
        },
        documents,
      });
      toast.success("Request submitted");
      navigate(`/confirmation/${res.reference_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed. Please try again.");
      setSubmitting(false);
    }
  };

  const nav = [
    ["Home", "/"], ["Our Services", "/services"], ["About Us", "/about"],
    ["Projects", "/projects"], ["Request a Survey", "/request"],
    ["Contact", "/contact"],
  ];

  const steps = [
    { icon: FileText, title: "We review your request", desc: "Our team will look over your project details and any submitted documents." },
    { icon: MessageSquare, title: "We confirm the project scope", desc: "A representative will contact you to discuss your needs, ask questions, and clarify the scope." },
    { icon: CalendarDays, title: "You receive a quote and schedule", desc: "We'll provide a detailed quote and timeline based on your project requirements." },
  ];

  return (
    <div className="min-h-screen bg-[#f4f5f4]">
      {/* HEADER */}
      <header className="relative z-50 bg-white">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Brand />
          <nav className="hidden items-center gap-6 xl:flex">
            {nav.map(([label, href]) => {
              const active = label === "Request a Survey";
              const cls = `relative py-1 text-sm font-medium transition-colors ${active ? "text-[#163323]" : "text-[#3d4742] hover:text-[#163323]"}`;
              return href.startsWith("/") ? (
                <Link key={label} to={href} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`} className={cls}>
                  {label}
                  {active && <span className="absolute -bottom-0.5 left-0 h-0.5 w-full rounded bg-[#c8a452]" />}
                </Link>
              ) : (
                <a key={label} href={href} className={cls}>{label}</a>
              );
            })}
          </nav>
          <div className="hidden items-center gap-4 lg:flex">
            <a href="mailto:dataearthsurveying@gmail.com">
              <Button className="h-10 rounded-md bg-[#163323] px-5 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#0d2116]">
                Get in Touch <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Button>
            </a>
            <span className="h-8 w-px bg-[#e6eae7]" />
            <span className="text-[10px] font-medium uppercase leading-tight tracking-[0.18em] text-[#5a6660]">
              Land Insights<br />For a Brighter<br />Tomorrow
            </span>
          </div>
          <MobileNav buttonClassName="rounded-full bg-[#163323] p-2 text-white xl:hidden" hideClass="xl:hidden" />
        </div>
      </header>

      {/* TITLE BAND */}
      <div className="relative overflow-hidden border-t border-[#e6eae7] bg-[#eef0ee]">
        <TopoBackground stroke="#163323" opacity={0.06} />
        <div className="relative mx-auto flex max-w-[1400px] flex-col gap-3 px-4 py-10 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:px-8">
          <div>
            <h1 className="font-serif text-4xl font-bold text-[#163323] sm:text-5xl">Request a Survey</h1>
            <p className="mt-3 max-w-2xl text-base text-[#4b544f]">
              Tell us about your property and project. We'll review the details and contact you to
              discuss the scope, timeline, and next steps.
            </p>
          </div>
          <div className="hidden text-right lg:block">
            <div className="font-serif text-sm font-semibold uppercase leading-relaxed tracking-[0.15em] text-[#163323]">
              People.<br />Land.
            </div>
            <div className="mt-1 font-serif text-sm uppercase tracking-[0.15em] text-[#b38f3f]">
              A Higher Perspective.
            </div>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="mx-auto grid max-w-[1400px] gap-6 px-4 py-8 sm:px-6 lg:grid-cols-3 lg:px-8">
        {/* FORM */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="rounded-xl border border-[#e6eae7] bg-white p-6 shadow-sm sm:p-8 lg:col-span-2"
        >
          {/* Contact Information */}
          <SectionHead icon={User} title="Contact Information" eyebrow="Let's get started" />
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label="Full Name" required>
              <Input data-testid="input-full-name" className={inputCls} placeholder="Enter your full name"
                value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
            </Field>
            <Field label="Company Name (Optional)">
              <Input data-testid="input-company" className={inputCls} placeholder="Enter company name"
                value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
            </Field>
            <Field label="Phone Number" required>
              <Input data-testid="input-phone" className={inputCls} placeholder="Enter your phone number"
                value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
            <Field label="Email Address" required>
              <Input data-testid="input-email" type="email" className={inputCls} placeholder="Enter your email address"
                value={form.email} onChange={(e) => set("email", e.target.value)} />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Preferred Contact Method" required>
              <RadioGroup data-testid="radio-contact-method" value={form.preferred_contact_method}
                onValueChange={(v) => set("preferred_contact_method", v)} className="grid grid-cols-3 gap-3 sm:max-w-md">
                {CONTACT_METHODS.map((m) => (
                  <label key={m} className={`flex cursor-pointer items-center gap-2 rounded-md border p-2.5 text-sm transition-all ${
                    form.preferred_contact_method === m ? "border-[#c8a452] bg-[#f6f1e3]" : "border-[#d2dad5] hover:border-[#c8a452]/50"
                  }`}>
                    <RadioGroupItem value={m} className="border-[#163323] text-[#163323]" /> {m}
                  </label>
                ))}
              </RadioGroup>
            </Field>
          </div>

          {/* Property Information */}
          <div className="mt-10">
            <SectionHead icon={MapPin} title="Property Information" eyebrow="Tell us about the property" />
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label="Property Address or Location" required>
              <Input data-testid="input-address" className={inputCls} placeholder="Enter the property address or location"
                value={form.address} onChange={(e) => set("address", e.target.value)} />
            </Field>
            <Field label="Island / Country" required>
              <Select value={form.island} onValueChange={(v) => set("island", v)}>
                <SelectTrigger data-testid="select-island" className={inputCls}><SelectValue placeholder="Select island or country" /></SelectTrigger>
                <SelectContent className="max-h-72">{locationOptions}</SelectContent>
              </Select>
            </Field>
            <Field label="Lot or Parcel Number (If Known)">
              <Input data-testid="input-lot" className={inputCls} placeholder="Enter lot or parcel number"
                value={form.lot_parcel_number} onChange={(e) => set("lot_parcel_number", e.target.value)} />
            </Field>
            <Field label="Property Size (If Known)">
              <Input data-testid="input-size" className={inputCls} placeholder="Enter property size (e.g. acres or sq ft)"
                value={form.property_size} onChange={(e) => set("property_size", e.target.value)} />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Site-Access Instructions (Optional)">
              <Textarea data-testid="input-access" rows={2} className={textareaCls}
                placeholder="Gate codes, contact on arrival, terrain notes, etc."
                value={form.site_access_instructions} onChange={(e) => set("site_access_instructions", e.target.value)} />
            </Field>
          </div>

          {/* Survey Details */}
          <div className="mt-10">
            <SectionHead icon={FileText} title="Survey Details" eyebrow="Share your project details" />
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label="Service Needed" required>
              <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                <SelectTrigger data-testid="select-service" className={inputCls}><SelectValue placeholder="Select a surveying service" /></SelectTrigger>
                <SelectContent>{SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Preferred Completion Date">
              <Input data-testid="input-date" type="date" className={inputCls}
                value={form.preferred_service_date} onChange={(e) => set("preferred_service_date", e.target.value)} />
            </Field>
          </div>

          {isConstruction && (
            <div className="mt-5 space-y-5 rounded-lg border border-[#e6eae7] bg-[#f8f9f8] p-4">
              <Field label="Project Type" required>
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                  {PROJECT_TYPES.map((t) => (
                    <button key={t} type="button" data-testid={`project-type-${t}`} onClick={() => set("project_type", t)}
                      className={`rounded-md border p-2.5 text-sm font-medium transition-all ${
                        form.project_type === t ? "border-[#c8a452] bg-[#f6f1e3] text-[#163323]" : "border-[#d2dad5] bg-white text-[#5a6660] hover:border-[#c8a452]/50"
                      }`}>{t}</button>
                  ))}
                </div>
              </Field>
              <Field label="Work Required" required>
                <div className="grid gap-2 sm:grid-cols-2">
                  {WORK_REQUIRED.map((w) => {
                    const active = form.work_required.includes(w);
                    return (
                      <label key={w} data-testid={`work-${w}`} className={`flex cursor-pointer items-center gap-2.5 rounded-md border p-2.5 text-sm transition-all ${
                        active ? "border-[#c8a452] bg-[#f6f1e3]" : "border-[#d2dad5] bg-white hover:border-[#c8a452]/50"
                      }`}>
                        <Checkbox checked={active} onCheckedChange={() => toggleWork(w)}
                          className="border-[#163323] data-[state=checked]:border-[#163323] data-[state=checked]:bg-[#163323]" />
                        {w}
                      </label>
                    );
                  })}
                </div>
              </Field>
              <Field label="Current Construction Stage">
                <Select value={form.construction_stage} onValueChange={(v) => set("construction_stage", v)}>
                  <SelectTrigger data-testid="select-stage" className={inputCls}><SelectValue placeholder="Select stage" /></SelectTrigger>
                  <SelectContent>{CONSTRUCTION_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          )}

          <div className="mt-5">
            <Field label="Project Description" required>
              <Textarea data-testid="input-description" rows={3} className={textareaCls}
                placeholder="Tell us about your project, goals, and any specific requirements..."
                value={form.project_description} onChange={(e) => set("project_description", e.target.value)} />
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Contractor / Site Manager Information (Optional)">
              <Input data-testid="input-contractor" className={inputCls} placeholder="Name, company, phone of on-site contact"
                value={form.contractor_info} onChange={(e) => set("contractor_info", e.target.value)} />
            </Field>
          </div>

          {/* Upload */}
          <div className="mt-5">
            <Label className="text-sm font-medium text-[#1a221e]">Upload Plans, Drawings, Deeds, or Site Photos</Label>
            <label data-testid="dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              className={`mt-1.5 flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed p-6 text-center transition-all ${
                dragOver ? "border-[#c8a452] bg-[#f6f1e3]" : "border-[#c4ccc7] bg-[#f8f9f8] hover:border-[#c8a452]/60"
              }`}>
              <UploadCloud className="h-7 w-7 text-[#163323]" />
              <span className="mt-2 text-sm text-[#3d4742]"><strong>Choose files</strong> or drag and drop</span>
              <span className="mt-0.5 text-xs text-[#9aa5a0]">PDF, JPG, PNG, or DOC — optional</span>
              <input data-testid="file-input" type="file" multiple accept={ACCEPTED_TYPES} className="hidden"
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
            </label>
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((f) => (
                  <div key={f.tempId} data-testid="file-item" className="flex items-center gap-3 rounded-md border border-[#e6eae7] bg-white p-2.5">
                    <FileText className="h-4 w-4 shrink-0 text-[#8e6e23]" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-[#1a221e]">{f.original_filename}</div>
                      <div className="text-xs text-[#9aa5a0]">{f.uploading ? `Uploading… ${f.progress}%` : humanSize(f.size)}</div>
                    </div>
                    {f.uploading ? <Loader2 className="h-4 w-4 animate-spin text-[#c8a452]" />
                      : <button data-testid="remove-file" onClick={() => removeFile(f.tempId)} className="text-[#9aa5a0] hover:text-[#c0392b]"><X className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Confirm + submit */}
          <label className="mt-5 flex items-start gap-3">
            <Checkbox data-testid="confirm-checkbox" checked={confirmed} onCheckedChange={setConfirmed}
              className="mt-0.5 border-[#163323] data-[state=checked]:border-[#163323] data-[state=checked]:bg-[#163323]" />
            <span className="text-sm text-[#1a221e]">I confirm that the information provided is accurate. <span className="text-[#c0392b]">*</span></span>
          </label>
          <Button data-testid="submit-request-btn" onClick={submit} disabled={submitting}
            className="mt-5 h-13 w-full rounded-md bg-[#b3892f] py-3 text-base font-semibold text-white hover:bg-[#96721f] disabled:opacity-60">
            {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting…</> : <>Submit Survey Request <ArrowRight className="ml-2 h-5 w-5" /></>}
          </Button>
        </motion.div>

        {/* SIDEBAR */}
        <motion.aside
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="rounded-xl border border-[#e6eae7] bg-white p-5 shadow-sm"
        >
          <div className="relative overflow-hidden rounded-lg">
            <img src={img("request_sidebar")} alt="Data Earth surveyor with total station" className="h-56 w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-l from-[#0d2116]/85 via-[#0d2116]/30 to-transparent" />
            <div className="absolute right-4 top-4 text-right text-[11px] font-semibold uppercase leading-[1.7] tracking-[0.22em] text-white">
              Measure<br />Map<br />Solve<br /><span className="text-[#c8a452]">Build a Brighter<br />Tomorrow</span>
            </div>
          </div>

          <h3 className="mt-6 font-serif text-2xl font-bold text-[#163323]">What Happens Next?</h3>
          <div className="mt-5 space-y-6">
            {steps.map((s, i) => (
              <div key={s.title} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-[#c8a452] font-mono text-sm font-bold text-[#b38f3f]">{i + 1}</span>
                  {i < steps.length - 1 && <span className="mt-1 h-full w-0.5 flex-1 bg-[#e5d5ab]" />}
                </div>
                <div className="pb-1">
                  <div className="flex items-center gap-2">
                    <s.icon className="h-4 w-4 text-[#163323]" />
                    <h4 className="text-sm font-bold text-[#163323]">{s.title}</h4>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-[#5a6660]">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-start gap-3 rounded-lg bg-[#eef0ee] p-4">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#163323] text-white"><Info className="h-4 w-4" /></span>
            <p className="text-sm text-[#3d4742]">
              <strong>Submitting this form does not confirm a booking.</strong> A representative will contact you before work is scheduled.
            </p>
          </div>

          <div className="mt-6 text-right">
            <div className="font-serif text-sm font-semibold uppercase tracking-[0.15em] text-[#163323]">Same Ground.</div>
            <div className="font-serif text-sm uppercase tracking-[0.15em] text-[#b38f3f]">A Brighter Tomorrow.</div>
            <span className="mt-2 ml-auto block h-0.5 w-16 rounded bg-[#c8a452]" />
          </div>
        </motion.aside>
      </div>

      {/* FOOTER */}
      <footer className="relative overflow-hidden bg-[#0d2116] text-white">
        <TopoBackground opacity={0.06} />
        <div className="relative mx-auto flex max-w-[1400px] flex-col items-center gap-5 px-4 py-6 sm:px-6 lg:flex-row lg:justify-between lg:px-8">
          <div className="flex items-center gap-5">
            <Brand light />
            <span className="hidden h-8 w-px bg-white/20 sm:block" />
            <span className="hidden font-serif text-sm italic text-white/80 sm:block">Accurate. Reliable. Built for Your Project.</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            <Link to="/" className="transition-colors hover:text-[#c8a452]">Home</Link>
            <Link to="/services" className="transition-colors hover:text-[#c8a452]">Our Services</Link>
            <Link to="/request" className="transition-colors hover:text-[#c8a452]">Request a Survey</Link>
            <button onClick={openModal} data-testid="footer-admin-link" className="transition-colors hover:text-[#c8a452]">Admin Portal</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
